/**
 * Formatting Utilities
 *
 * For date formatting, always pass the timezone parameter to ensure dates are
 * displayed in org timezone, not browser local time.
 *
 * @example
 * // CORRECT - uses org timezone
 * formatDateAsDate(date, timezone)
 *
 * // INCORRECT - uses browser timezone
 * formatDateAsDate(date)
 *
 * See docs/TIMEZONE_STANDARDS.md for comprehensive timezone handling documentation.
 *
 * @module formatters
 */
import dayjs from "dayjs";
import CustomParseFormat from "dayjs/plugin/customParseFormat";
import take from "lodash/take";
import pluralize from "pluralize";

dayjs.extend(CustomParseFormat);

type Granularity = "year" | "month" | "week" | "day" | "hour" | "minute" | "second";
type Conjunction = "and" | "or";

export type HumanizeListOptions = {
  readonly conjunction?: Conjunction | null;
  readonly oxfordComma?: boolean;
  readonly maxIntervals?: number;
};

export const humanizeList = (values: string[], options?: HumanizeListOptions): string => {
  const intervals = options?.maxIntervals ? take(values, options.maxIntervals) : values;
  switch (intervals.length) {
    case 0:
      return "";
    case 1:
      return intervals[0] === undefined ? "" : intervals[0];
    default:
      const conj: Conjunction | null = options?.conjunction === undefined ? "and" : options?.conjunction;
      if (conj) {
        let s = intervals.slice(0, -1).join(", ");
        if (intervals.length >= 3 && options?.oxfordComma) {
          s += ",";
        }
        return `${s} ${conj} ${intervals[intervals.length - 1]}`;
      }
      return intervals.join(", ");
  }
};

type HumanReadableTimeSinceParams = HumanizeListOptions & {
  readonly maxGranularity?: Granularity;
  readonly minGranularity?: Granularity;
  readonly maxIntervals?: number;
};

/**
 * Returns a human readable representation of the difference between two date {@link Date} objects.  If only one date, {@link Date}, object
 * is provided, the return will represent the human readable representation of the time between the current date/time and the provided date
 * object, {@link Date}.
 *
 * @param {[Date, Date] | Date} dates: The dates for which the time difference should be returned.
 * @param {HumanReadableTimeSinceParams} options: The options for the human readable time difference.
 * @returns {string}
 */
export const humanReadableTimeDifference = (
  dates: [Date, Date] | Date,
  {
    maxGranularity = "day",
    minGranularity = "year",
    oxfordComma = true,
    conjunction = "and",
    maxIntervals = 2,
  }: HumanReadableTimeSinceParams,
): string => {
  /* The number of seconds for a given granularity.  Dividing the numeric timestamp by the interval yields the count of that granularity.
     e.g. Difference / 2628000 = The number of months in the difference. */
  const timeIntervals = [31536000, 2628000, 604800, 86400, 3600, 60, 1];
  const granularities: Granularity[] = ["year", "month", "week", "day", "hour", "minute", "second"];

  const granularityIndexes: [number, number] = [granularities.indexOf(minGranularity), granularities.indexOf(maxGranularity)];
  if (granularityIndexes[0] > granularityIndexes[1]) {
    throw new TypeError(`The maximum granularity '${maxGranularity}' is less granular than the minimum granularity, '${minGranularity}'.`);
  }

  const gs: Granularity[] = granularities.slice(granularityIndexes[0], granularityIndexes[1] + 1);

  const t1 = Array.isArray(dates) ? dates[0] : dates;
  const t2 = Array.isArray(dates) ? dates[1] : Date.now();

  const t1Time = t1 instanceof Date ? t1.getTime() : t1;
  const t2Time = t2 instanceof Date ? t2.getTime() : t2;

  // Normalize the time difference in milliseconds to seconds.
  let diff = Math.abs(t2Time - t1Time) / 1000;

  type IntervalCount = { count: number; granularity: Granularity };
  const running: IntervalCount[] = gs.reduce((prev: IntervalCount[], g: Granularity, i: number): IntervalCount[] => {
    const ti = timeIntervals[i];
    if (!ti) {
      throw new Error(
        "Unexpectedly received undefined value for time interval in iterable.  The number of time intervals should equal the number " +
          "of granularities.",
      );
    }

    // The count of the number of intervals of that type in the leftover time difference.
    const intervalCount = diff / ti;
    diff = diff - Math.floor(intervalCount) * ti;

    if (Math.floor(intervalCount) !== 0) {
      return [...prev, { count: Math.floor(intervalCount), granularity: g }];
    }
    return prev;
  }, []);

  /* If the running interval count is empty, it means that either the two date-times are exactly the difference between them is less than
     the maximum granularity (i.e. the difference is 30 seconds when the maximum granularity is "minute"). */
  if (running.length === 0) {
    /* If  the two date-times are exactly the same or the difference is less than the maximum *possible* granularity, simply return a 0
       representation. */
    if (gs.length === granularities.length || diff === 0) {
      return `0 ${maxGranularity}s`;
    }
    /* If the difference is less than the maximum provided granularity, indicate as such. */
    const ti = timeIntervals[granularities.indexOf(maxGranularity)];
    if (ti === undefined || ti === 0) {
      throw new TypeError("Unexpected condition!");
    }
    const intervalCount = diff / ti;
    return `< ${Math.ceil(intervalCount)} ${maxGranularity}${intervalCount > 1 ? "s" : ""}`;
  }

  return humanizeList(
    running.map((r: { count: number; granularity: Granularity }) => `${r.count} ${pluralize(r.granularity, r.count)}`),
    { oxfordComma, conjunction, maxIntervals },
  );
};

const countDollarFractionDigits = (dollarAmount: string): number => {
  const dotIndex = dollarAmount.indexOf(".");
  if (dotIndex === -1) {
    return 0;
  }
  return dollarAmount.length - dotIndex - 1;
};

/** Convert parsed dollar amount to cents, preserving sub-cent precision when the input has >2 decimal places. */
const dollarsToCentsString = (signedDollars: number, dollarAmount: string): string => {
  const centsFractionDigits = Math.max(0, countDollarFractionDigits(dollarAmount) - 2);
  const scale = 10 ** centsFractionDigits;
  const cents = signedDollars * 100;
  return String(Math.round(cents * scale) / scale);
};

// parse a human readable currency string to cents (see docs/DECIMAL_PRECISION_STANDARDS.md § Costs vs. Amounts)
export const parseCurrency = (value: string): string => {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return "0";
  }

  const parenMatch = /^\s*\((.+)\)\s*$/.exec(trimmed);
  const parenthesizedBody = parenMatch?.[1];
  const isParenthesizedNegative = parenthesizedBody !== undefined;
  let working = isParenthesizedNegative ? parenthesizedBody.trim() : trimmed;

  // Reject digit groups separated by whitespace before collapse would weld them.
  if (/\d\s+\d/.test(working)) {
    return "0";
  }

  working = working.replace(/,/g, "").replace(/\s+/g, "");
  working = working.replace(/^[A-Z]{2,3}(?=[$€£¥₹])/i, "").replace(/^(?:[$€£¥₹][A-Z]{2,3}|[A-Z]{2,3}|[$€£¥₹])+/, "");

  let isNegative = false;
  if (working.startsWith("-")) {
    isNegative = true;
    working = working.slice(1).trim();
  }
  working = working.replace(/^(?:[$€£¥₹][A-Z]{2,3}|[A-Z]{2,3}(?=[$€£¥₹])|[A-Z]{2,3}|[$€£¥₹])+/, "");

  const trailingDecimalMatch = /^(\d+)\.$/.exec(working);
  if (trailingDecimalMatch) {
    const dollars = Number(trailingDecimalMatch[1]);
    if (!Number.isFinite(dollars)) {
      return "0";
    }
    const signed = isParenthesizedNegative || isNegative ? -Math.abs(dollars) : dollars;
    return dollarsToCentsString(signed, trailingDecimalMatch[0]);
  }

  if (!/^(?:\d+(?:\.\d+)?|\.\d+)$/.test(working)) {
    return "0";
  }

  const parsed = parseFloat(working);
  if (!Number.isFinite(parsed)) {
    return "0";
  }

  const signed = isParenthesizedNegative || isNegative ? -Math.abs(parsed) : parsed;
  return dollarsToCentsString(signed, working);
};

/**
 * Format a value in cents to a human-readable currency string.
 *
 * Supports `bigint` values from Prisma BigInt columns (e.g., Order.totalAmount, OrderItem.totalAmount).
 *
 * **Precision Warning:** BigInt values are converted to number via `Number()`. Values exceeding
 * `Number.MAX_SAFE_INTEGER` (~$90 trillion in cents) will lose precision silently. This is
 * acceptable for display since such values indicate a data bug, but this function will NOT
 * throw an error - it will display an approximate value.
 *
 * @param value - Amount in cents (string, number, or bigint)
 * @param precision - Maximum decimal places (default: 2)
 * @param minimumPrecision - Minimum decimal places (default: 2)
 * @param parenthesisNegatives - Show negative values as (amount) instead of -amount
 * @returns Formatted currency string (e.g., "$1,234.56")
 *
 * @example
 * formatCurrency(12345)           // "$123.45"
 * formatCurrency(BigInt(12345))   // "$123.45" - handles Prisma BigInt columns
 * formatCurrency(-500, 2, 2, true) // "($5.00)"
 *
 * ## Multi-currency display contract (SC-1865)
 *
 * **Legacy:** this helper hardcodes `currency: "USD"`. Do not use for new financial UI.
 *
 * **Phase D target:** format with `Org.baseCurrencyCode` (single-currency / FX flag off) or
 * `MoneyAmount.currencyCode` / `baseCurrencyCode` (FX flag on). Org base-currency formatting
 * is **not** gated by the org FX LaunchDarkly flag — only foreign txn currency, dual display,
 * rate freeze, and GL conversion are.
 *
 * Canonical: `src/modules/multi-currency/ui/README.md` § Display contract.
 */
export const formatCurrency = (
  value: string | number | bigint,
  precision = 2,
  minimumPrecision = 2,
  parenthesisNegatives = false,
): string => formatCurrencyForCode(value, "USD", precision, minimumPrecision, parenthesisNegatives);

export const formatCurrencyForCode = (
  value: string | number | bigint,
  currencyCode: string,
  precision = 2,
  minimumPrecision = 2,
  parenthesisNegatives = false,
): string => {
  if (value === "") {
    return "";
  }
  const normalizedCurrencyCode = currencyCode.trim().toUpperCase() || "USD";
  const n = typeof value === "string" ? parseFloat(value) || 0 : typeof value === "bigint" ? Number(value) : value;
  if (n === 0 || Object.is(n, -0)) {
    // Normalize negative zero; honor requested precision on zero amounts.
    const zeroPrecision = minimumPrecision ?? precision;
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: normalizedCurrencyCode,
      minimumFractionDigits: zeroPrecision,
      maximumFractionDigits: precision,
    }).format(0);
  }

  if (!parenthesisNegatives) {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: normalizedCurrencyCode,
      minimumFractionDigits: minimumPrecision ?? precision,
      maximumFractionDigits: precision,
    }).format(n / 100);
  }

  const isNegative = n < 0;
  const formattedCurrency = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: normalizedCurrencyCode,
    minimumFractionDigits: minimumPrecision ?? precision,
    maximumFractionDigits: precision,
  }).format(Math.abs(n) / 100);

  return isNegative ? `(${formattedCurrency})` : formattedCurrency;
};

const dateFormat: Intl.DateTimeFormatOptions = { year: "numeric", month: "2-digit", day: "2-digit" };
const timeFormat: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };
const dateTimeFormat = { ...dateFormat, ...timeFormat };

export const formatDateAsIsoDate = (value: Date): string => value.toISOString().substring(0, 10);

export const formatDateAsDate = (value: Date, timeZone?: string): string =>
  value.toLocaleDateString(undefined, { ...dateFormat, timeZone });

export const formatDateAsTime = (value: Date): string => value.toLocaleTimeString(undefined, timeFormat);

export const formatDateAsDateTime = (value: Date, timezone?: string): string =>
  value.toLocaleString(undefined, { ...dateTimeFormat, timeZone: timezone });

export const formatNumber = (value: number, props?: Intl.NumberFormatOptions | undefined): string => value.toLocaleString(undefined, props);

export const formatTimeStringAsTime = (value: string): string => formatDateAsTime(dayjs(value, "HH:mm").toDate());

export function formatDiscountPercentage(value: { toNumber(): number } | number | null | undefined): string {
  if (value == null) {
    return "";
  }
  const n = typeof value === "number" ? value : value.toNumber();
  return `${formatNumber(n, { maximumFractionDigits: 2 })}%`;
}

/** Minimal shape required to format a person's display name. */
export type RepName = { firstName: string; lastName: string | null };

/** Returns a trimmed full name, omitting the last name when absent. */
export const formatRepName = (rep: RepName): string => [rep.firstName, rep.lastName].filter(Boolean).join(" ");
